import showHideCustomReusableModal from "@iewcasia/Store/Handlers/CustomModals/ShowHideCustomReusableModal";
import { CustomState } from "@iewcasia/Store/Reducers";
import { SystemListModel } from "@iewcasia/Types/CustomApiModels";
import { buttonCommon } from "@iewcasia/Utilities/styleguide";
import { colorStrings } from "@iewcasia/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps, StyledClickable } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code - Start */
import { setGA4Event } from "@iewcasia/Store/Handlers/Utilities/CustomTracking";
/* Valtech Custom Code - End */

interface OwnProps {
    id: "return-request" | "change-order" | "place-order" | "requote" | "edit-quote" | "cancel-quote";
    headline: string;
    subHeadline: string;
    dropdownHint?: string;
    messageHint?: string;
    submitLabel: string;
    cancelLabel: string;
    errorMessage:
        | string
        | React.DetailedReactHTMLElement<{}, HTMLElement>
        | React.DetailedReactHTMLElement<{}, HTMLElement>[];
    extendedStyles?: CustomReusableModalStyles;
    submitRequest: (parameter: any) => void;
    // Valtech Custom Code
    trackingDetails?: {
        trackingKey: string, 
        status: string, 
        orderType: string, 
        value: number | string, 
        quantity?: number
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomState, ownProps: OwnProps) => ({
    modalIsOpen: state.customModals.modal[ownProps.id],
    options: state.customSystemList[ownProps.id],
});

const mapDispatchToProps = {
    showHideCustomReusableModal,
};

export interface CustomReusableModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    subHeadingText?: TypographyProps;
    responseWrapper?: InjectableCss;
    responseSelect?: SelectPresentationProps;
    messageWrapper?: InjectableCss;
    messageTextArea?: TextAreaPresentationProps;
    actionsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    submitButtonLoading?: LoadingSpinnerProps;
    submitButtonLabel?: TypographyPresentationProps;
    cancelButton?: ButtonPresentationProps;
    errorMessage?: InjectableCss;
    errorMessageLabel?: TypographyPresentationProps;

    disclaimer?: TypographyProps;
    disclaimerLink?: LinkPresentationProps;
}

export const customReusableModalStyles: CustomReusableModalStyles = {
    modal: {
        size: 600,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 15px;
        `,
    },
    subHeadingText: {
        variant: "body",
        forwardAs: "span",
    },
    responseWrapper: {
        css: css`
            max-width: 280px;
        `,
    },
    messageWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    actionsWrapper: {
        css: css`
            display: inline-flex;
            gap: 20px;
        `,
    },
    submitButton: {
        variant: "primary",
        css: css`
            align-items: center;
            display: inline-flex;
            gap: 10px;
            justify-content: center;
            min-width: 200px;
        `,
    },
    submitButtonLoading: {
        size: 18,
    },
    submitButtonLabel: {
        ...buttonCommon.typographyProps,
    },
    cancelButton: {
        variant: "tertiary",
    },
    errorMessageLabel: {
        color: colorStrings.dangerMain,
    },

    disclaimer: {
        variant: "legend",
        forwardAs: "span",
        css: css`
            ${StyledClickable} {
                ${TypographyStyle} {
                    font-size: inherit;
                    line-height: inherit;
                }
            }
        `,
    },
};

const CustomReusableModal = ({
    id,
    headline,
    subHeadline,
    dropdownHint,
    messageHint,
    submitLabel,
    cancelLabel,
    errorMessage,
    submitRequest,
    extendedStyles,
    modalIsOpen,
    options,
    showHideCustomReusableModal,
    // Valtech Custom Code
    trackingDetails,
}: Props) => {
    const [styles] = useState(() => mergeToNew(customReusableModalStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);
    const [hasChanged, setHasChanged] = useState(false);
    const [responseInsite, setResponseInsite] = useState("");
    const [responseSF, setResponseSF] = useState("");
    const [message, setMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showError, setShowError] = useState(false);

    const modalCloseHandler = () => {
        if (isSubmitting) {
            return;
        }

        showHideCustomReusableModal({ key: id, show: false });
        setHasChanged(false);
        setResponseInsite("");
        setResponseSF("");
        setMessage("");
        setIsSubmitting(false);
        setShowError(false);
    };

    const handleResponseChange: SelectProps["onChange"] = event => {
        setHasChanged(true);
        const selected = event.currentTarget.value;
        const selectedOption = options?.find((option: SystemListModel) => option.description === selected);
        setResponseInsite(selectedOption?.description ?? "");
        setResponseSF(selectedOption?.additionalInfo ?? "");
    };

    const handleMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setHasChanged(true);
        setMessage(event.currentTarget.value);
    };

    const handleSubmitRequest = () => {
        setHasChanged(true);

        const checkDropdown = (options ?? []).length > 0 && responseInsite === "";
        if (checkDropdown || message === "" || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        submitRequest({
            message,
            responseInsite,
            responseSF,
            onError: () => {
                setIsSubmitting(false);
                setShowError(true);
            },
            onSuccess: () => {
                setIsSubmitting(false);
                /* Valtech Custom Code - Start */
                if (trackingDetails?.trackingKey) {
                    const { trackingKey, status, orderType, value, quantity } = trackingDetails;
                    setGA4Event([], trackingKey, { status, orderType, value, quantity });
                }
                /* Valtech Custom Code - End */
                // Verndale Customization Ticket:IEIXOP-4340
                if (id === "return-request") {
                    const fixedMessage = message.replace(/\n/g, ',');
                    setGA4Event([], "return_request_order", { returnOrderReason: responseInsite, itemsToReturn: fixedMessage });
                }
                // End Verndale Customization
                toasterContext.addToast({
                    body: siteMessage("CustomModals_GenericSuccessMessage"),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
                modalCloseHandler();
            },
        });
    };

    return (
        <Modal
            {...styles.modal}
            headline={headline}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
            data-test-selector={`${id}--modal`}
        >
            <StyledWrapper {...styles.innerWrapper}>
                <Typography {...styles.subHeadingText}>{subHeadline}</Typography>

                {(options ?? []).length > 0 && (
                    <StyledWrapper {...styles.responseWrapper}>
                        <Select
                            {...styles.responseSelect}
                            required={true}
                            value={responseInsite}
                            onChange={handleResponseChange}
                            error={
                                hasChanged && responseInsite === "" ? siteMessage("Field_Required", "Response") : null
                            }
                            data-test-selector={`${id}--modal__response`}
                        >
                            <option value="">{dropdownHint ?? translate("Select an option")}</option>
                            {options?.map((option: SystemListModel) => (
                                <option key={option.name} value={option.description}>
                                    {option.description}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.messageWrapper}>
                    <TextArea
                        {...styles.messageTextArea}
                        required={true}
                        value={message}
                        placeholder={messageHint ?? translate("Enter a message")}
                        onChange={handleMessageChange}
                        error={hasChanged && message === "" ? siteMessage("Field_Required", "Message") : null}
                        data-test-selector={`${id}--modal__message`}
                    />

                    {id === "return-request" && (
                        <Typography {...styles.disclaimer}>{siteMessage("ReturnRequestModal_Disclaimer")}</Typography>
                    )}
                </StyledWrapper>

                <StyledWrapper {...styles.actionsWrapper}>
                    <Button {...styles.submitButton} onClick={handleSubmitRequest}>
                        {isSubmitting && <LoadingSpinner {...styles.submitButtonLoading} />}
                        <Typography {...styles.submitButtonLabel}>{submitLabel}</Typography>
                    </Button>

                    <Button {...styles.cancelButton} onClick={modalCloseHandler} disabled={isSubmitting}>
                        {cancelLabel}
                    </Button>
                </StyledWrapper>

                {showError && (
                    <StyledWrapper {...styles.errorMessage}>
                        <Typography {...styles.errorMessageLabel}>{errorMessage}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomReusableModal as any);
